import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Select from "react-select";
import { toast } from "react-toastify";
import ImageUpload from "../components/ImageUpload";
import { getCampaignPreview } from "../services/api";
import CampaignPreviewModal from "../components/CampaignPreviewModal";
import ColdOutreachAnalytics from "./ColdOutreachAnalytics";

import {
    extractData,
    emailCSV,
    fetchScraperCampaignSummaries,
    startColdOutreach,
    trackColdOutreach,
} from "../redux/scraperSlice";

import ScraperLoader from "../components/ScraperLoader";

import {
    previewCleanup,
    deleteCleanup,
    getScraperCategories,
    getScraperSubcategories,
    getScraperCampaigns,
    updateCampaignCategories,
    previewScraperImport,
    importScraperFile,
} from "../services/api";
import SummaryCard from "../components/UI/SummaryCard";

import {
    AppButton,
    AppInput,
    AppSelect,
    TabBar,
    FilterChip,
    SectionCard,
    StatusBanner,
    ProgressBar,
    DataTable,
    ConfirmModal,
    CampaignDropdown,
    FieldRow,
} from "../components/scraper/index";

// ── react-select shared style ─────────────────────────────────────────────────
const RS = {
    control: (base, state) => ({
        ...base,
        minHeight: 42,
        borderRadius: 8,
        borderColor: state.isFocused ? "#3b82f6" : "#e5e7eb",
        boxShadow: state.isFocused ? "0 0 0 2px rgba(59,130,246,0.3)" : "none",
        "&:hover": { borderColor: "#93c5fd" },
    }),
};

const ATTACHMENT_MAX_SIZE_MB =
    Number(process.env.REACT_APP_OUTREACH_ATTACHMENT_SIZE_MB) || 5;

// ── Static option arrays ──────────────────────────────────────────────────────
const TABS = [
    { id: "scraping", label: "Scraping", icon: "🌐" },
    { id: "import", label: "Import", icon: "📥" },
    { id: "campaign-management", label: "Campaign Management", icon: "📁" },
    { id: "cold-outreach", label: "Cold Outreach", icon: "📧" },
    { id: "cleanup", label: "Cleanup", icon: "🧹" },
    { id: "outreach-analytics", label: "Outreach Analytics", icon: "📊" },
];

const DOWNLOAD_TYPE_OPTIONS = [
    { value: "all", label: "All" },
    { value: "email", label: "Emails" },
    { value: "phone", label: "Phones" },
    { value: "instagram", label: "Instagrams" },
];

const FROM_DOMAIN_OPTIONS = [
    { value: "artsalescompany", label: "Books & Shops — Art Sales Company" },
    { value: "artmediaconnect", label: "Curators & Sponsors — Art Media Connect" },
    { value: "artservicesonline", label: "Organizations & Artists — Art Services Online" },
];

const CLEANUP_TYPE_OPTIONS = [
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
    { value: "instagram", label: "Instagram" },
];

const CLEANUP_LIMIT_OPTIONS = [1000, 5000, 10000, 25000, 50000].map((n) => ({
    value: n, label: n.toLocaleString(),
}));

const QUILL_MODULES = {
    toolbar: [
        [{ header: "1" }, { header: "2" }, { font: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["bold", "italic", "underline"],
        [{ align: [] }],
        ["link", "image"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        ["blockquote", "code-block"],
        [{ direction: "rtl" }],
    ],
};

// =============================================================================
//  Scraper page
// =============================================================================
const Scraper = () => {
    const dispatch = useDispatch();
    const {
        loading,            // extractData only
        summariesLoading,   // fetchScraperCampaignSummaries only
        emailing,
        emailStatusMessage,
        progress,
        error,
        scrapingCampaignSummaries,
        coldOutreachLoading,
        coldOutreachError,
    } = useSelector((state) => state.scraper);

    // ── Scraping form ────────────────────────────────────────────────────────
    const [url, setUrl] = useState("");
    const [campaignName, setCampaignName] = useState("");
    const [pages, setPages] = useState(1000);
    const [downloadType, setDownloadType] = useState("all");
    const [filterWebsite, setFilterWebsite] = useState("");
    const [filterCampaign, setFilterCampaign] = useState("");
    const [newCampaignCategories, setNewCampaignCategories] = useState([]);
    const [newCampaignSubcats, setNewCampaignSubcats] = useState([]);

    // ── Cold outreach ────────────────────────────────────────────────────────
    const [selectedCampaigns, setSelectedCampaigns] = useState([]);
    const [coldSubject, setColdSubject] = useState("");
    const [coldContent, setColdContent] = useState("");
    const [coldProgress, setColdProgress] = useState(null);
    const [targetedSegment, setTargetedSegment] = useState("artsalescompany");
    const [isTrackingJob, setIsTrackingJob] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const trackingRef = useRef(null);
    const toggleNativeAudience = (key) => {

        setSelectedCampaigns(prev => {

            if (prev.includes(key)) {

                return prev.filter(
                    item => item !== key
                );
            }

            return [...prev, key];
        });
    };

    // ── Cleanup ──────────────────────────────────────────────────────────────
    const [cleanupType, setCleanupType] = useState("email");
    const [cleanupLimit, setCleanupLimit] = useState(1000);
    const [cleanupPreview, setCleanupPreview] = useState(null);
    const [cleanupLoading, setCleanupLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [cleanupMessage, setCleanupMessage] = useState("");
    const [showConfirm, setShowConfirm] = useState(false);

    // ── Campaign management ──────────────────────────────────────────────────
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [selectedCampaignIds, setSelectedCampaignIds] = useState([]); // multi
    const [mgmtCategories, setMgmtCategories] = useState([]);
    const [mgmtSubcategories, setMgmtSubcategories] = useState([]);
    const [saveMappingLoading, setSaveMappingLoading] = useState(false);

    // ── Navigation + filter tabs ─────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState("scraping");
    const [activeCategoryTab, setActiveCategoryTab] = useState("all");
    const [activeSubcategoryTab, setActiveSubcategoryTab] = useState("all");

    // Import
    const [importFile, setImportFile] = useState(null);
    const [importColumns, setImportColumns] = useState([]);
    const [importLoading, setImportLoading] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [importCampaignName, setImportCampaignName] = useState("");
    const [importMappings, setImportMappings] = useState({ email: "", phone: "", instagram: "", website: "", first_name: "", last_name: "" });

    const [campaignPreviewOpen, setCampaignPreviewOpen] = useState(false);
    const [campaignPreviewLoading, setCampaignPreviewLoading] = useState(false);
    const [campaignPreviewData, setCampaignPreviewData] = useState(null);
    const [campaignPreviewTab, setCampaignPreviewTab] = useState("emails");
    const [previewCampaignLoading, setPreviewCampaignLoading] = useState(null);




    // ── Bootstrap ────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchScraperCampaignSummaries());
        loadCampaignData();
    }, [dispatch]);

    useEffect(() => {
        if (activeTab === "campaign-management") loadCampaignData();
    }, [activeTab]);

    useEffect(() => () => {
        if (trackingRef.current) clearInterval(trackingRef.current);
    }, []);

    const loadCampaignData = async () => {
        const [catRes, subRes, campRes] = await Promise.all([
            getScraperCategories(),
            getScraperSubcategories(),
            getScraperCampaigns(),
        ]);
        setCategories(catRes.data || []);
        setSubcategories(subRes.data || []);
        setCampaigns(campRes.data || []);
    };

    // ── Derived ──────────────────────────────────────────────────────────────
    const categoryFilterTabs = [
        { id: "all", label: "All" },
        ...categories.map((c) => ({ id: c.id, label: c.name })),
        { id: "uncategorized", label: "Uncategorized" },
    ];

    const visibleSubcategoryChips =
        activeCategoryTab === "all"
            ? []
            : subcategories.filter((s) => s.category_id === activeCategoryTab);

    const createSubcatOptions = subcategories
        .filter((s) => newCampaignCategories.includes(s.category_id))
        .map((s) => ({ value: s.id, label: s.name }));

    const mgmtSubcatOptions = subcategories
        .filter((s) => mgmtCategories.includes(s.category_id))
        .map((s) => ({ value: s.id, label: s.name }));

    const campaignSummaryMap = {};
    (scrapingCampaignSummaries?.scraping_summary || []).forEach((item) => {
        campaignSummaryMap[item.clean_campaign] = item;
    });

    const internalCampaignGroup = {
        id: "internal",
        name: "Internal Campaigns",

        subcategories: [
            {
                id: "internal-audience",
                name: "Registered Users",

                campaigns: [
                    {
                        id: "native_artists",
                        campaign_name: "Registered Artists",
                        email_count:
                            scrapingCampaignSummaries?.total_artists || 0
                    },

                    {
                        id: "native_organizations",
                        campaign_name: "Registered Organizations",
                        email_count:
                            scrapingCampaignSummaries?.total_organizations || 0
                    },

                    {
                        id: "native_subscribers",
                        campaign_name: "Registered Subscribers",
                        email_count:
                            scrapingCampaignSummaries?.total_subscribers || 0
                    }
                ]
            }
        ]
    };

    const groupedCampaigns = [
        internalCampaignGroup,
        ...categories
            .map((cat) => ({
                ...cat,
                subcategories: subcategories
                    .filter((s) => s.category_id === cat.id)
                    .map((s) => ({
                        ...s,
                        campaigns: campaigns.filter((c) => c.subcategories?.some((sc) => sc.id === s.id)).map((campaign) => ({
                            ...campaign,

                            email_count:
                                campaignSummaryMap[
                                    campaign.campaign_name
                                ]?.total_emails || 0
                        })),
                    }))
                    .filter((s) => s.campaigns.length > 0),
            }))
            .filter((cat) => cat.subcategories.length > 0)];

    const uncategorizedCampaigns = campaigns.filter((c) => !c.categories?.length);

    const filteredCampaigns = campaigns.filter((c) => {
        if (activeCategoryTab === "uncategorized") return !c.categories?.length;
        if (activeCategoryTab !== "all") {
            if (!c.categories?.some((cat) => cat.id === Number(activeCategoryTab))) return false;
        }
        if (activeSubcategoryTab !== "all") {
            return c.subcategories?.some((s) => s.id === Number(activeSubcategoryTab));
        }
        return true;
    });

    const isColdBusy =
        coldOutreachLoading ||
        isTrackingJob ||
        (coldProgress && coldProgress.status !== "COMPLETED" && coldProgress.status !== "PARTIAL" && coldProgress.status !== "FAILED");

    // ── Scraping ──────────────────────────────────────────────────────────────
    const handleExtract = (e) => {
        e.preventDefault();
        if (!url || pages < 1) return;
        if (!newCampaignCategories.length) { toast.error("Please select a category"); return; }
        if (!newCampaignSubcats.length) { toast.error("Please select a subcategory"); return; }
        dispatch(extractData({
            url,
            pages,
            campaign_name: campaignName,
            category_ids: newCampaignCategories,
            subcategory_ids: newCampaignSubcats,
        }));
    };

    const handleSendCSV = () =>
        dispatch(emailCSV({ type: downloadType, website: filterWebsite, campaign: filterCampaign }));

    // ── Campaign management ───────────────────────────────────────────────────
    const handleSaveCampaignMapping = async () => {
        if (!selectedCampaignIds.length) { toast.error("Select at least one campaign"); return; }
        if (!mgmtCategories.length) { toast.error("Select a category"); return; }
        if (!mgmtSubcategories.length) { toast.error("Select a subcategory"); return; }
        try {
            setSaveMappingLoading(true);
            // fire all campaign updates in parallel
            await Promise.all(
                selectedCampaignIds.map((campaign_id) =>
                    updateCampaignCategories({
                        campaign_id,
                        category_ids: mgmtCategories,
                        subcategory_ids: mgmtSubcategories,
                    })
                )
            );
            toast.success(
                selectedCampaignIds.length > 1
                    ? `Mapping saved for ${selectedCampaignIds.length} campaigns`
                    : "Campaign mapping saved"
            );
            setSelectedCampaignIds([]);
            setMgmtCategories([]);
            setMgmtSubcategories([]);
            await loadCampaignData();
        } catch {
            toast.error("Failed to save mapping");
        } finally {
            setSaveMappingLoading(false);
        }
    };


    const getCampaignValue = (campaign) => {

        return (
            typeof campaign.id === "string" &&
            campaign.id.startsWith("native_")
        )
            ? campaign.id
            : campaign.campaign_name;
    };

    // ── Cold outreach ─────────────────────────────────────────────────────────
    const toggleCampaign = (name) =>
        setSelectedCampaigns((p) => p.includes(name) ? p.filter((n) => n !== name) : [...p, name]);

    const toggleSubcategory = (sub) => {

        const names =
            sub.campaigns.map(
                getCampaignValue
            );

        const allSel =
            names.every(
                (n) =>
                    selectedCampaigns.includes(n)
            );

        setSelectedCampaigns((p) =>
            allSel
                ? p.filter(
                    (n) => !names.includes(n)
                )
                : [...new Set([
                    ...p,
                    ...names
                ])]
        );
    };


    const toggleCategory = (cat) => {

        const names =
            cat.subcategories.flatMap(
                (s) =>
                    s.campaigns.map(
                        getCampaignValue
                    )
            );

        const allSel =
            names.every(
                (n) =>
                    selectedCampaigns.includes(n)
            );

        setSelectedCampaigns((p) =>
            allSel
                ? p.filter(
                    (n) => !names.includes(n)
                )
                : [...new Set([
                    ...p,
                    ...names
                ])]
        );
    };

    const handleSendEmails = async () => {
        if (!coldSubject || !coldContent || !selectedCampaigns.length) return;
        setColdProgress(null);
        setIsTrackingJob(false);
        if (trackingRef.current) { clearInterval(trackingRef.current); trackingRef.current = null; }
        try {
            const res = await dispatch(startColdOutreach({
                outreach_email_subject: coldSubject,
                outreach_email_html: coldContent,
                outreach_email_keys: selectedCampaigns,
                targeted_segment: targetedSegment,
                attachments,
            }));
            if (res?.payload?.job_id) { setIsTrackingJob(true); trackJobStatus(res.payload.job_id); }
            else setIsTrackingJob(false);
        } catch { setIsTrackingJob(false); }
    };

    const trackJobStatus = (jobId) => {
        trackingRef.current = setInterval(async () => {
            const res = await dispatch(trackColdOutreach(jobId));
            console.log("Polling Response:", res);
            console.log("Status:", res?.payload?.status);
            if (res?.payload?.status === "COMPLETED" || res?.payload?.status === "FAILED" || res?.payload?.status === "PARTIAL") {
                console.log("Stopping polling");
                setColdProgress(res.payload);
                setIsTrackingJob(false);
                clearInterval(trackingRef.current);
                trackingRef.current = null;
            } else if (res?.payload) {
                setColdProgress(res.payload);
            } else if (res?.error) {
                setIsTrackingJob(false);
                clearInterval(trackingRef.current);
                trackingRef.current = null;
            }
        }, 3000);
    };

    // ── Cleanup ───────────────────────────────────────────────────────────────
    const handlePreviewCleanup = async () => {
        try {
            setCleanupLoading(true);
            setCleanupMessage("");
            setCleanupPreview(null);
            const data = await previewCleanup(cleanupType, cleanupLimit);
            if (data.invalid_count === 0) {
                setCleanupMessage(`No invalid ${cleanupType} records found.`);
                toast.info(`No invalid ${cleanupType} records found.`);
                return;
            }
            setCleanupPreview(data);
        } catch {
            toast.error("Failed to load cleanup preview");
        } finally {
            setCleanupLoading(false);
        }
    };

    const performDelete = async () => {
        try {
            setDeleteLoading(true);
            const result = await deleteCleanup(cleanupType, cleanupLimit);
            toast.success(`${result.deleted_count} invalid records deleted`);
            setCleanupPreview(null);
            setShowConfirm(false);
            dispatch(fetchScraperCampaignSummaries());
        } catch {
            toast.error("Failed to delete records");
        } finally {
            setDeleteLoading(false);
        }
    };


    const handlePreviewImport = async () => {

        if (!importFile) {
            toast.error("Please select a file");
            return;
        }

        try {

            setImportLoading(true);

            const data =
                await previewScraperImport(
                    importFile
                );

            setImportColumns(
                data.columns || []
            );

            toast.success(
                "Columns loaded successfully"
            );

        } catch (error) {

            toast.error(
                error?.response?.data?.error ||
                "Failed to preview file"
            );

        } finally {

            setImportLoading(false);
        }
    };


    const handleImportData = async () => {

        if (!importCampaignName) {

            toast.error(
                "Campaign name is required"
            );

            return;
        }

        const hasMapping =
            importMappings.email ||
            importMappings.phone ||
            importMappings.instagram;

        if (!hasMapping) {

            toast.error(
                "Select at least one mapping"
            );

            return;
        }

        try {

            setImportLoading(true);

            const data =
                await importScraperFile({
                    file: importFile,
                    campaign_name:
                        importCampaignName,
                    mappings:
                        importMappings
                });

            setImportResult(data);

            toast.success(
                "Import completed"
            );

            dispatch(
                fetchScraperCampaignSummaries()
            );

        } catch (error) {

            toast.error(
                error?.response?.data?.error ||
                "Import failed"
            );

        } finally {

            setImportLoading(false);
        }
    };


    // ── Table column definitions ──────────────────────────────────────────────
    const campaignTableCols = [
        { key: "campaign_name", label: "Campaign" },
        { key: "emails", label: "Emails", render: (_, r) => campaignSummaryMap[r.campaign_name]?.total_emails ?? 0 },
        { key: "phones", label: "Phones", render: (_, r) => campaignSummaryMap[r.campaign_name]?.total_phones ?? 0 },
        { key: "instagrams", label: "Instagrams", render: (_, r) => campaignSummaryMap[r.campaign_name]?.total_instagrams ?? 0 },
        { key: "created", label: "Created", render: (_, r) => campaignSummaryMap[r.campaign_name]?.created_at || "—" },
        { key: "updated", label: "Updated", render: (_, r) => campaignSummaryMap[r.campaign_name]?.updated_at || "—" },
        {
            key: "actions",
            label: "Preview",
            render: (_, row) => (
                <AppButton
                    size="sm"
                    variant="primary"
                    loading={
                        previewCampaignLoading ===
                        row.campaign_name
                    }
                    onClick={() =>
                        openCampaignPreview(
                            row.campaign_name
                        )
                    }
                >
                    View
                </AppButton>
            )
        }
    ];

    const mgmtTableCols = [
        { key: "campaign_name", label: "Campaign" },
        { key: "categories", label: "Categories", render: (_, r) => r.categories?.map((x) => x.name).join(", ") || "—" },
        { key: "subcategories", label: "Subcategories", render: (_, r) => r.subcategories?.map((x) => x.name).join(", ") || "—" },
    ];

    const cleanupTableCols = [
        { key: "id", label: "ID" },
        { key: "value", label: "Value" },
    ];


    const openCampaignPreview =
        async (campaignName) => {

            try {

                setPreviewCampaignLoading(campaignName);

                setCampaignPreviewLoading(true);

                setCampaignPreviewOpen(true);

                const data = await getCampaignPreview(campaignName);

                setCampaignPreviewData(data);

            } catch (error) {

                toast.error(
                    "Failed to load campaign data"
                );

            } finally {

                setCampaignPreviewLoading(false);
                setPreviewCampaignLoading(null);
            }
        };


    // ─────────────────────────────────────────────────────────────────────────
    //  Render
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="container mx-auto px-4 py-6 max-w-7xl">

            {/* ── Loader: page-level summaries fetch (on mount) ── */}
            {summariesLoading && (
                <ScraperLoader text="Loading campaign data, please wait…" />
            )}

            {/* ── Loader: extract job submitted, waiting for job_id back ── */}
            {loading && (
                <ScraperLoader text="Submitting scraping job, please wait…" />
            )}

            {/* ── Loader: polling in progress (progress popup) ── */}
            {!loading && progress && progress.status !== "COMPLETED" && progress.status !== "FAILED" && (
                <ScraperLoader text="Extracting data, please wait…" progress={progress} />
            )}

            {/* ── Loader: sending CSV ── */}
            {emailing && !coldProgress && (
                <ScraperLoader text="Sending CSV to admin…" />
            )}

            {/* ── Loader: cold outreach running ── */}
            {isColdBusy && (
                <ScraperLoader
                    text={coldOutreachLoading ? "Starting email campaign…" : "Sending outreach emails…"}
                    progress={coldProgress}
                />
            )}

            {/* Tab navigation */}
            <TabBar tabs={TABS} active={activeTab} onChange={setActiveTab} />

            {/* ══════════════════════════════════════════════════════
                TAB: SCRAPING
            ══════════════════════════════════════════════════════ */}
            {activeTab === "scraping" && (
                <div className="space-y-6">

                    <SectionCard title="New Scraping Job" icon="🌐">
                        <form onSubmit={handleExtract}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 items-end">

                                <AppInput
                                    label="Campaign Name" id="campaign-name" required
                                    placeholder="e.g. Art Galleries NYC"
                                    value={campaignName} onChange={(e) => setCampaignName(e.target.value)}
                                    className="xl:col-span-1"
                                />
                                <AppInput
                                    label="Website URL" id="url" required
                                    placeholder="https://example.com"
                                    value={url} onChange={(e) => setUrl(e.target.value)}
                                    className="xl:col-span-1"
                                />
                                <AppInput
                                    label="Pages" id="pages" type="number" required min={1}
                                    placeholder="1000"
                                    value={pages} onChange={(e) => setPages(Number(e.target.value))}
                                    className="xl:col-span-1"
                                />

                                <div className="flex flex-col gap-1 xl:col-span-1">
                                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                        Categories <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        isMulti styles={RS}
                                        options={categories.map((c) => ({ value: c.id, label: c.name }))}
                                        value={categories
                                            .filter((c) => newCampaignCategories.includes(c.id))
                                            .map((c) => ({ value: c.id, label: c.name }))}
                                        onChange={(sel) => {
                                            const ids = sel ? sel.map((s) => s.value) : [];
                                            setNewCampaignCategories(ids);
                                            setNewCampaignSubcats((prev) => prev.filter((sid) => {
                                                const sub = subcategories.find((s) => s.id === sid);
                                                return sub && ids.includes(sub.category_id);
                                            })
                                            );
                                        }}
                                        placeholder="Select…"
                                    />
                                </div>

                                <div className="flex flex-col gap-1 xl:col-span-1">
                                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                        Subcategories <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        isMulti styles={RS}
                                        options={createSubcatOptions}
                                        value={createSubcatOptions.filter((o) => newCampaignSubcats.includes(o.value))}
                                        onChange={(sel) => setNewCampaignSubcats(sel ? sel.map((s) => s.value) : [])}
                                        placeholder="Select…"
                                        isDisabled={!newCampaignCategories.length}
                                    />
                                </div>

                                <div className="xl:col-span-1 flex items-end">
                                    <AppButton type="submit" loading={loading} variant="primary" className="w-full">
                                        Extract
                                    </AppButton>
                                </div>
                            </div>
                        </form>
                    </SectionCard>

                    {/* Banners */}
                    {progress?.status === "COMPLETED" && (
                        <StatusBanner type="success">
                            Scraping complete for <strong>{campaignName || url}</strong>
                            <div className="mt-2 space-y-1">
                                <FieldRow label="Status" value={progress.status} />
                                <FieldRow label="Pages" value={`${progress.pages_scraped} / ${progress.total_pages}`} />
                            </div>
                        </StatusBanner>
                    )}
                    {progress?.status === "FAILED" && (
                        <StatusBanner type="error">
                            Scraping failed for <strong>{campaignName || url}</strong>
                            {progress.error_message && (
                                <p className="mt-1 text-sm">{progress.error_message}</p>
                            )}
                        </StatusBanner>
                    )}
                    {emailStatusMessage && <StatusBanner type="info">{emailStatusMessage}</StatusBanner>}
                    {error && <StatusBanner type="error">{error}</StatusBanner>}

                    {/* Export */}
                    <SectionCard title="Export Data" icon="📤">
                        <div className="flex flex-wrap gap-3 items-end">
                            <AppSelect
                                label="Data type" id="download-type"
                                value={downloadType} onChange={(e) => setDownloadType(e.target.value)}
                                options={DOWNLOAD_TYPE_OPTIONS} className="w-36"
                            />
                            <AppInput
                                label="Filter by campaign" id="filter-campaign" placeholder="Optional"
                                value={filterCampaign} onChange={(e) => setFilterCampaign(e.target.value)}
                                className="flex-1 min-w-[180px]"
                            />
                            <AppInput
                                label="Filter by website" id="filter-website" placeholder="Optional"
                                value={filterWebsite} onChange={(e) => setFilterWebsite(e.target.value)}
                                className="flex-1 min-w-[180px]"
                            />
                            <div className="flex items-end">
                                <AppButton
                                    variant="success"
                                    onClick={handleSendCSV}
                                    loading={emailing ? "Sending…" : false}
                                    icon="📩"
                                >
                                    Send CSV to Admin
                                </AppButton>
                            </div>
                        </div>
                    </SectionCard>

                    {scrapingCampaignSummaries && (

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

                            <div className="bg-white rounded-2xl border-t-4 border-blue-500 shadow-sm p-5">
                                <div className="text-sm font-medium text-gray-500">
                                    👨‍🎨 Registered Artists
                                </div>

                                <div className="mt-2 text-3xl font-bold text-blue-600">
                                    {scrapingCampaignSummaries?.total_artists || 0}
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl border-t-4 border-green-500 shadow-sm p-5">
                                <div className="text-sm font-medium text-gray-500">
                                    🏢 Registered Organizations
                                </div>

                                <div className="mt-2 text-3xl font-bold text-green-600">
                                    {scrapingCampaignSummaries?.total_organizations || 0}
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl border-t-4 border-purple-500 shadow-sm p-5">
                                <div className="text-sm font-medium text-gray-500">
                                    📧 Registered Subscribers
                                </div>

                                <div className="mt-2 text-3xl font-bold text-purple-600">
                                    {scrapingCampaignSummaries?.total_subscribers || 0}
                                </div>
                            </div>

                        </div>

                    )}

                    {/* Campaign summaries */}
                    {scrapingCampaignSummaries?.scraping_summary?.length > 0 && (
                        <SectionCard title="Campaign Summaries" icon="📊">

                            <div className="flex flex-wrap gap-2 mb-4">
                                {categoryFilterTabs.map((tab) => (
                                    <FilterChip
                                        key={tab.id} label={tab.label}
                                        active={activeCategoryTab === tab.id} color="blue"
                                        onClick={() => {
                                            setActiveCategoryTab(tab.id);
                                            setActiveSubcategoryTab("all");
                                        }}
                                    />
                                ))}
                            </div>

                            {activeCategoryTab !== "all" && activeCategoryTab !== "uncategorized" && (
                                <div className="flex flex-wrap gap-2 mb-4">
                                    <FilterChip
                                        label="All" active={activeSubcategoryTab === "all"} color="green"
                                        onClick={() => setActiveSubcategoryTab("all")}
                                    />
                                    {visibleSubcategoryChips.map((s) => (
                                        <FilterChip
                                            key={s.id} label={s.name}
                                            active={activeSubcategoryTab === s.id} color="green"
                                            onClick={() => setActiveSubcategoryTab(s.id)}
                                        />
                                    ))}
                                </div>
                            )}

                            <DataTable
                                columns={campaignTableCols}
                                rows={filteredCampaigns.map((c) => ({ ...c, __key: c.id }))}
                                emptyText="No campaigns match the current filter"
                            />
                        </SectionCard>
                    )}
                </div>
            )}

            {/* ══════════════════════════════════════════════════════
                TAB: CAMPAIGN MANAGEMENT
            ══════════════════════════════════════════════════════ */}
            {activeTab === "campaign-management" && (
                <div className="space-y-6">
                    <SectionCard title="Edit Campaign Mapping" icon="✏️">

                        {/* Inline row: Campaigns (multi) | Categories | Subcategories | Save */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-end">

                            {/* Multi-campaign react-select */}
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                    Campaigns <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    isMulti styles={RS}
                                    options={campaigns.map((c) => ({ value: c.id, label: c.campaign_name }))}
                                    value={campaigns
                                        .filter((c) => selectedCampaignIds.includes(c.id))
                                        .map((c) => ({ value: c.id, label: c.campaign_name }))}
                                    onChange={(sel) => {
                                        const ids = sel ? sel.map((s) => s.value) : [];
                                        setSelectedCampaignIds(ids);
                                        // pre-fill cats/subcats only when exactly one campaign chosen
                                        if (ids.length === 1) {
                                            const found = campaigns.find((c) => c.id === ids[0]);
                                            if (found) {
                                                setMgmtCategories(found.categories.map((x) => x.id));
                                                setMgmtSubcategories(found.subcategories.map((x) => x.id));
                                            }
                                        } else {
                                            setMgmtCategories([]);
                                            setMgmtSubcategories([]);
                                        }
                                    }}
                                    placeholder="Select campaigns…"
                                />
                            </div>

                            {/* Categories react-select multi */}
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                    Categories <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    isMulti styles={RS}
                                    options={categories.map((c) => ({ value: c.id, label: c.name }))}
                                    value={categories
                                        .filter((c) => mgmtCategories.includes(c.id))
                                        .map((c) => ({ value: c.id, label: c.name }))}
                                    onChange={(sel) => {
                                        const ids = sel ? sel.map((s) => s.value) : [];
                                        setMgmtCategories(ids);
                                        setMgmtSubcategories((prev) =>
                                            prev.filter((sid) => {
                                                const sub = subcategories.find((s) => s.id === sid);
                                                return sub && ids.includes(sub.category_id);
                                            })
                                        );
                                    }}
                                    placeholder="Select categories…"
                                />
                            </div>

                            {/* Subcategories react-select multi */}
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                    Subcategories <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    isMulti styles={RS}
                                    options={mgmtSubcatOptions}
                                    value={mgmtSubcatOptions.filter((o) => mgmtSubcategories.includes(o.value))}
                                    onChange={(sel) => setMgmtSubcategories(sel ? sel.map((s) => s.value) : [])}
                                    placeholder={mgmtCategories.length ? "Select subcategories…" : "Select a category first"}
                                    isDisabled={!mgmtCategories.length}
                                />
                            </div>

                            {/* Save */}
                            <div className="flex items-end">
                                <AppButton
                                    variant="primary"
                                    onClick={handleSaveCampaignMapping}
                                    loading={saveMappingLoading ? "Saving…" : false}
                                    className="w-full"
                                >
                                    Save Mapping
                                </AppButton>
                            </div>
                        </div>
                    </SectionCard>

                    <SectionCard title="All Campaign Mappings" icon="📋">
                        <DataTable
                            columns={mgmtTableCols}
                            rows={campaigns.map((c) => ({ ...c, __key: c.id }))}
                            emptyText="No campaigns found"
                        />
                    </SectionCard>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════
                TAB: COLD OUTREACH
            ══════════════════════════════════════════════════════ */}
            {activeTab === "cold-outreach" && (
                <SectionCard title="Cold Email Outreach" icon="📧">
                    <div className="space-y-5 max-w-3xl">

                        <AppInput
                            label="Subject line" id="cold-subject"
                            placeholder="Enter email subject…"
                            value={coldSubject} onChange={(e) => setColdSubject(e.target.value)}
                        />

                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Email body
                            </label>
                            <ReactQuill
                                value={coldContent} onChange={setColdContent}
                                theme="snow" modules={QUILL_MODULES}
                                className="rounded-lg border border-gray-200 overflow-hidden"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <AppSelect
                                label="Send from domain" id="from-domain"
                                value={targetedSegment} onChange={(e) => setTargetedSegment(e.target.value)}
                                options={FROM_DOMAIN_OPTIONS}
                            />
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                    Campaigns
                                </label>

                                <CampaignDropdown
                                    groupedCampaigns={groupedCampaigns}
                                    uncategorized={uncategorizedCampaigns}
                                    selected={selectedCampaigns}
                                    onToggleCampaign={toggleCampaign}
                                    onToggleSubcategory={toggleSubcategory}
                                    onToggleCategory={toggleCategory}
                                />
                            </div>
                        </div>

                        {/* Attachments — contained card so it sits cleanly in the flow */}
                        <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                            <ImageUpload
                                label="Attachments"
                                initialImages={attachments}
                                onImageUpload={setAttachments}
                                onError={(msg) => toast.error(msg)}
                                maxSizeMB={ATTACHMENT_MAX_SIZE_MB}
                                acceptedTypes={[
                                    "image/jpeg",
                                    "image/png",
                                    "image/jpg",
                                    "application/pdf",
                                    "application/msword",
                                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                                ]}
                                accept=".jpeg,.jpg,.png,.pdf,.doc,.docx"
                            />
                        </div>

                        <AppButton
                            variant="purple"
                            onClick={handleSendEmails}
                            loading={isColdBusy ? "Sending…" : false}
                            disabled={!coldSubject || !coldContent || !selectedCampaigns.length || isColdBusy}
                            icon="🚀"
                        >
                            Send Emails
                        </AppButton>

                        {coldOutreachError && (
                            <StatusBanner type="error">{coldOutreachError}</StatusBanner>
                        )}

                        {coldProgress && (coldProgress.status === "COMPLETED" || coldProgress.status === "FAILED" || coldProgress.status === "PARTIAL") && (
                            <div
                                className={`border rounded-xl p-4 space-y-2 ${coldProgress.status === "COMPLETED"
                                    ? "bg-emerald-50 border-emerald-200"
                                    : coldProgress.status === "PARTIAL"
                                        ? "bg-amber-50 border-amber-200"
                                        : "bg-red-50 border-red-200"
                                    }`}
                            >
                                <FieldRow label="Status" value={coldProgress.status} />
                                {coldProgress.total !== undefined && (
                                    <ProgressBar
                                        label="Emails sent"
                                        value={coldProgress.success || 0}
                                        total={coldProgress.total}
                                        extra={[
                                            { label: "Success", value: coldProgress.success || 0 },
                                            { label: "Failed", value: coldProgress.failed || 0 }
                                        ]}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </SectionCard>
            )}

            {/* ══════════════════════════════════════════════════════
                TAB: CLEANUP
            ══════════════════════════════════════════════════════ */}
            {activeTab === "cleanup" && (
                <SectionCard title="Scraped Data Cleanup" icon="🧹">
                    <div className="space-y-5">

                        {cleanupMessage && (
                            <StatusBanner type="success">{cleanupMessage}</StatusBanner>
                        )}

                        <div className="flex flex-wrap gap-3 items-end">
                            <AppSelect
                                label="Data type" id="cleanup-type"
                                value={cleanupType} onChange={(e) => setCleanupType(e.target.value)}
                                options={CLEANUP_TYPE_OPTIONS} className="w-36"
                            />
                            <AppSelect
                                label="Batch limit" id="cleanup-limit"
                                value={cleanupLimit} onChange={(e) => setCleanupLimit(Number(e.target.value))}
                                options={CLEANUP_LIMIT_OPTIONS} className="w-36"
                            />
                            <div className="flex items-end">
                                <AppButton
                                    variant="primary"
                                    onClick={handlePreviewCleanup}
                                    loading={cleanupLoading ? "Checking…" : false}
                                    disabled={cleanupLoading}
                                >
                                    Preview Invalid Records
                                </AppButton>
                            </div>
                        </div>

                        {cleanupPreview && (
                            <>
                                <StatusBanner type="warning">
                                    Found <strong>{cleanupPreview.invalid_count}</strong> invalid {cleanupType} records.{" "}
                                    <span className="text-gray-600">
                                        Showing first {cleanupPreview.examples?.length || 0}.
                                    </span>
                                </StatusBanner>

                                <DataTable
                                    columns={cleanupTableCols}
                                    rows={(cleanupPreview.examples || []).map((r) => ({ ...r, __key: r.id }))}
                                    emptyText="No examples"
                                />

                                <AppButton
                                    variant="danger"
                                    onClick={() => setShowConfirm(true)}
                                    loading={deleteLoading ? "Deleting…" : false}
                                    disabled={deleteLoading || cleanupPreview.invalid_count === 0}
                                    icon="🗑️"
                                >
                                    Delete {cleanupPreview.invalid_count} Invalid Records
                                </AppButton>
                            </>
                        )}
                    </div>
                </SectionCard>
            )}

            {activeTab === "import" && (

                <div className="space-y-6">

                    {/* Upload File */}

                    <SectionCard title="Import Contacts">

                        <div className="space-y-4">

                            <div>

                                <label
                                    className="
                            block
                            text-sm
                            font-medium
                            text-gray-700
                            mb-2
                        "
                                >
                                    CSV / XLSX File
                                </label>

                                <input
                                    type="file"
                                    accept=".csv,.xlsx"
                                    onChange={(e) => {

                                        const file =
                                            e.target.files?.[0];

                                        setImportFile(file);

                                        setImportColumns([]);

                                        setImportResult(null);
                                    }}
                                />

                            </div>

                            <AppButton
                                onClick={
                                    handlePreviewImport
                                }
                                loading={
                                    importLoading
                                }
                            >
                                Preview Columns
                            </AppButton>

                        </div>

                    </SectionCard>

                    {/* Mapping Section */}

                    {importColumns.length > 0 && (

                        <SectionCard
                            title="Column Mapping"
                        >

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                <AppInput
                                    label="Campaign Name"
                                    value={
                                        importCampaignName
                                    }
                                    onChange={(e) =>
                                        setImportCampaignName(
                                            e.target.value
                                        )
                                    }
                                />

                                <div />

                                <AppSelect
                                    label="Email Column"
                                    value={
                                        importMappings.email
                                    }
                                    onChange={(e) =>
                                        setImportMappings(
                                            prev => ({
                                                ...prev,
                                                email:
                                                    e.target.value
                                            })
                                        )
                                    }
                                    placeholder="Select"
                                    options={
                                        importColumns.map(
                                            col => ({
                                                value: col,
                                                label: col
                                            })
                                        )
                                    }
                                />

                                <AppSelect
                                    label="Phone Column"
                                    value={
                                        importMappings.phone
                                    }
                                    onChange={(e) =>
                                        setImportMappings(
                                            prev => ({
                                                ...prev,
                                                phone:
                                                    e.target.value
                                            })
                                        )
                                    }
                                    placeholder="Select"
                                    options={
                                        importColumns.map(
                                            col => ({
                                                value: col,
                                                label: col
                                            })
                                        )
                                    }
                                />

                                <AppSelect
                                    label="Instagram Column"
                                    value={
                                        importMappings.instagram
                                    }
                                    onChange={(e) =>
                                        setImportMappings(
                                            prev => ({
                                                ...prev,
                                                instagram:
                                                    e.target.value
                                            })
                                        )
                                    }
                                    placeholder="Select"
                                    options={
                                        importColumns.map(
                                            col => ({
                                                value: col,
                                                label: col
                                            })
                                        )
                                    }
                                />

                                <AppSelect
                                    label="Website Column"
                                    value={
                                        importMappings.website
                                    }
                                    onChange={(e) =>
                                        setImportMappings(
                                            prev => ({
                                                ...prev,
                                                website:
                                                    e.target.value
                                            })
                                        )
                                    }
                                    placeholder="Select"
                                    options={
                                        importColumns.map(
                                            col => ({
                                                value: col,
                                                label: col
                                            })
                                        )
                                    }
                                />

                                <AppSelect
                                    label="First Name Column"
                                    value={
                                        importMappings.first_name
                                    }
                                    onChange={(e) =>
                                        setImportMappings(
                                            prev => ({
                                                ...prev,
                                                first_name:
                                                    e.target.value
                                            })
                                        )
                                    }
                                    placeholder="Select"
                                    options={
                                        importColumns.map(
                                            col => ({
                                                value: col,
                                                label: col
                                            })
                                        )
                                    }
                                />

                                <AppSelect
                                    label="Last Name Column"
                                    value={
                                        importMappings.last_name
                                    }
                                    onChange={(e) =>
                                        setImportMappings(
                                            prev => ({
                                                ...prev,
                                                last_name:
                                                    e.target.value
                                            })
                                        )
                                    }
                                    placeholder="Select"
                                    options={
                                        importColumns.map(
                                            col => ({
                                                value: col,
                                                label: col
                                            })
                                        )
                                    }
                                />

                            </div>

                            <div className="mt-6">

                                <AppButton
                                    onClick={
                                        handleImportData
                                    }
                                    loading={
                                        importLoading
                                    }
                                >
                                    Import Data
                                </AppButton>

                            </div>

                        </SectionCard>

                    )}

                    {/* Result */}

                    {importResult && (

                        <StatusBanner
                            type="success"
                        >

                            <div className="space-y-1">

                                <div>
                                    Total Rows:
                                    {" "}
                                    <strong>
                                        {
                                            importResult.total_rows
                                        }
                                    </strong>
                                </div>

                                <div>
                                    Inserted:
                                    {" "}
                                    <strong>
                                        {
                                            importResult.inserted
                                        }
                                    </strong>
                                </div>

                                <div>
                                    Duplicates:
                                    {" "}
                                    <strong>
                                        {
                                            importResult.duplicates
                                        }
                                    </strong>
                                </div>

                                <div>
                                    Invalid:
                                    {" "}
                                    <strong>
                                        {
                                            importResult.invalid
                                        }
                                    </strong>
                                </div>

                            </div>

                        </StatusBanner>

                    )}

                </div>

            )}

            {
                activeTab === "outreach-analytics" && (
                    <ColdOutreachAnalytics />
                )
            }

            {/* Confirm delete modal */}
            <ConfirmModal
                isOpen={showConfirm}
                title="Delete invalid records?"
                message={`This will permanently delete ${cleanupPreview?.invalid_count || 0} invalid ${cleanupType} records. This cannot be undone.`}
                confirmLabel="Delete"
                variant="danger"
                confirmLoading={deleteLoading ? "Deleting…" : false}
                onConfirm={performDelete}
                onCancel={() => setShowConfirm(false)}
            />
            <CampaignPreviewModal
                isOpen={campaignPreviewOpen}
                loading={campaignPreviewLoading}
                data={campaignPreviewData}
                onClose={() => {

                    setCampaignPreviewOpen(false);

                    setCampaignPreviewData(null);
                }}
            />
        </div>
    );
};


export default Scraper;